import { Link } from '@backstage/core-components';
import { EntityRefLink } from '@backstage/plugin-catalog-react';
import { makeStyles } from '@material-ui/core';
import type { ListRow } from './listFilters';
import {
  clickOnEntry,
  LifecycleOf,
  OwnerOf,
  StarOf,
  Stars,
  TagsOf,
} from './rowParts';

/**
 * The list as a table: Name · System · Owner · Type · Lifecycle · Tags.
 *
 * A plain table rather than the framework's data table, because the rows have
 * to answer to the list's own keys — the one under the cursor is marked, and
 * `↵` opens it — and the framework's table brings a search, a pager and a
 * toolbar of its own that would stand beside the ones this page already has.
 *
 * On a phone it keeps the three columns a reader decides by — what it is, who
 * owns it, what kind of thing — and drops the rest rather than scrolling
 * sideways; the cards show everything at any width.
 *
 * MUI v4 only — no MUI v5.
 */

const useStyles = makeStyles(theme => ({
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    tableLayout: 'auto',
    fontSize: '0.875rem',
    '& th': {
      textAlign: 'left',
      fontWeight: 500,
      fontSize: '0.8rem',
      color: 'var(--ui-text-muted)',
      padding: theme.spacing(1.25, 1.5),
      borderBottom: '1px solid var(--ui-border)',
      whiteSpace: 'nowrap',
    },
    '& td': {
      padding: theme.spacing(1.5),
      borderBottom: '1px solid var(--ui-border)',
      verticalAlign: 'middle',
      color: 'var(--ui-text)',
      // Whole words: `anywhere` lets the table shrink a column below its
      // longest word, and at a laptop's width it did — "Platfo/rm Team".
      overflowWrap: 'break-word',
    },
    '& tbody tr:last-child td': { borderBottom: 'none' },
  },
  row: {
    cursor: 'pointer',
    '&:hover td': { background: 'var(--ui-nav-hover-bg)' },
  },
  active: {
    '& td': { background: 'var(--ui-nav-active-bg)' },
    '& td:first-child': {
      boxShadow: 'inset 3px 0 0 var(--ui-primary)',
    },
  },
  nameCell: { minWidth: 0, width: '36%' },
  nameLine: { display: 'flex', alignItems: 'center', gap: theme.spacing(0.5) },
  name: { fontWeight: 600, color: 'var(--ui-text)' },
  description: {
    marginTop: 2,
    fontSize: '0.8rem',
    color: 'var(--ui-text-muted)',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  muted: { color: 'var(--ui-text-muted)' },
  type: { color: 'var(--ui-text-muted)', whiteSpace: 'nowrap' },
  // The columns go as the page narrows rather than squeezing every word: the
  // tags below a wide laptop, system and lifecycle below a tablet. What is
  // left — name, owner, type — is what a reader decides by.
  wide: { [theme.breakpoints.down('md')]: { display: 'none' } },
  roomy: { [theme.breakpoints.down('sm')]: { display: 'none' } },
}));

export const ListTable = ({
  rows,
  active,
  pathOf,
  onOpen,
  stars,
}: {
  rows: ListRow[];
  active: number;
  pathOf: (row: ListRow) => string;
  onOpen: (index: number) => void;
  stars: Stars;
}) => {
  const classes = useStyles();
  return (
    <table className={classes.table} aria-label="Catalog">
      <thead>
        <tr>
          <th scope="col">Name</th>
          <th scope="col" className={classes.roomy}>
            System
          </th>
          <th scope="col">Owner</th>
          <th scope="col">Type</th>
          <th scope="col" className={classes.roomy}>
            Lifecycle
          </th>
          <th scope="col" className={classes.wide}>
            Tags
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row, index) => (
          <tr
            key={row.ref}
            data-list-row={index}
            aria-current={index === active ? 'true' : undefined}
            className={`${classes.row} ${
              index === active ? classes.active : ''
            }`}
            onClick={e => clickOnEntry(e, pathOf(row), () => onOpen(index))}
          >
            <td className={classes.nameCell}>
              <div className={classes.nameLine}>
                <Link to={pathOf(row)} className={classes.name}>
                  {row.title}
                </Link>
                <StarOf row={row} stars={stars} />
              </div>
              {row.description && (
                <div className={classes.description}>{row.description}</div>
              )}
            </td>
            <td className={classes.roomy}>
              {row.system ? (
                <EntityRefLink
                  entityRef={row.system}
                  defaultKind="system"
                  disableTooltip
                  hideIcon
                />
              ) : (
                <span className={classes.muted}>—</span>
              )}
            </td>
            <td>
              <OwnerOf row={row} />
            </td>
            <td className={classes.type}>{row.category || '—'}</td>
            <td className={classes.roomy}>
              <LifecycleOf row={row} />
            </td>
            <td className={classes.wide}>
              <TagsOf row={row} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};
